/**
 * Mapping Instrumentation
 *
 * Runtime tracking and validation of UI → Engine data flow.
 * This module provides:
 * - Change tracking (ring buffer of recent changes)
 * - Enum validation (catching mismatches immediately)
 * - Warning generation (dropped fields, unmapped questions)
 * - State snapshots for DevSidebar
 */

import type {
  MappingChangeEvent,
  MappingWarning,
  MappingWarningType,
  MappingDiagnosticsState,
  MappingLifecycleEvent,
  MappingEventType,
} from './types';
import {
  getMappingByQuestionId,
  validateEnumValue,
  getAllQuestionIds,
  questionMappings,
} from './registry';
import { isAllowlisted } from './allowlist';
import { convertToSystemRecInput } from './converter';

// =============================================================================
// CONSTANTS
// =============================================================================

/** Maximum number of change events to keep in the ring buffer */
const MAX_CHANGES = 50;

/** Maximum number of warnings to keep */
const MAX_WARNINGS = 100;

/** Maximum number of lifecycle events to keep in the ring buffer */
const MAX_LIFECYCLE_EVENTS = 50;

// =============================================================================
// STATE
// =============================================================================

/**
 * Global diagnostics state.
 * This is a singleton that tracks all mapping activity.
 */
let diagnosticsState: MappingDiagnosticsState = {
  lastChanges: [],
  warnings: [],
  rawInput: null,
  normalizedInput: null,
  enabled: false,
  lifecycleEvents: [],
  docContractVersion: null,
  migratedFromLegacy: false,
};

/**
 * Subscribers for state changes (for DevSidebar)
 */
type Subscriber = (state: MappingDiagnosticsState) => void;
const subscribers: Set<Subscriber> = new Set();

// =============================================================================
// STATE MANAGEMENT
// =============================================================================

/**
 * Get the current diagnostics state
 */
export function getDiagnosticsState(): MappingDiagnosticsState {
  return diagnosticsState;
}

/**
 * Enable or disable instrumentation
 */
export function setInstrumentationEnabled(enabled: boolean): void {
  diagnosticsState = { ...diagnosticsState, enabled };
  notifySubscribers();
}

/**
 * Subscribe to state changes
 */
export function subscribe(callback: Subscriber): () => void {
  subscribers.add(callback);
  return () => subscribers.delete(callback);
}

/**
 * Notify all subscribers of state change
 */
function notifySubscribers(): void {
  subscribers.forEach(cb => cb(diagnosticsState));
}

/**
 * Clear all diagnostics
 */
export function clearDiagnostics(): void {
  diagnosticsState = {
    lastChanges: [],
    warnings: [],
    rawInput: null,
    normalizedInput: null,
    enabled: diagnosticsState.enabled,
    lifecycleEvents: [],
    docContractVersion: null,
    migratedFromLegacy: false,
  };
  notifySubscribers();
}

// =============================================================================
// CHANGE TRACKING
// =============================================================================

/**
 * Record a change event
 */
function recordChange(event: MappingChangeEvent): void {
  if (!diagnosticsState.enabled) return;

  const changes = [...diagnosticsState.lastChanges, event];
  if (changes.length > MAX_CHANGES) {
    changes.shift();
  }

  diagnosticsState = { ...diagnosticsState, lastChanges: changes };
  notifySubscribers();
}

/**
 * Record a warning
 */
function recordWarning(warning: MappingWarning): void {
  if (!diagnosticsState.enabled) return;

  // Don't duplicate warnings with same message
  if (diagnosticsState.warnings.some(w => w.message === warning.message)) {
    return;
  }

  const warnings = [...diagnosticsState.warnings, warning];
  if (warnings.length > MAX_WARNINGS) {
    warnings.shift();
  }

  diagnosticsState = { ...diagnosticsState, warnings };
  notifySubscribers();
}

/**
 * Clear a specific warning type
 */
export function clearWarnings(type?: MappingWarningType): void {
  if (type) {
    diagnosticsState = {
      ...diagnosticsState,
      warnings: diagnosticsState.warnings.filter(w => w.type !== type),
    };
  } else {
    diagnosticsState = { ...diagnosticsState, warnings: [] };
  }
  notifySubscribers();
}

// =============================================================================
// LIFECYCLE EVENT TRACKING
// =============================================================================

/**
 * Track a lifecycle event in the mapping system
 */
export function trackLifecycleEvent(
  type: MappingEventType,
  source: string,
  options?: {
    docId?: string;
    keys?: number;
    note?: string;
    section?: string;
    fields?: string[];
    errorMessage?: string;
    errorStack?: string;
    offendingKeys?: string[];
  }
): void {
  if (!diagnosticsState.enabled) return;

  const event: MappingLifecycleEvent = {
    type,
    source,
    docId: options?.docId,
    keys: options?.keys,
    note: options?.note,
    section: options?.section,
    fields: options?.fields,
    errorMessage: options?.errorMessage,
    errorStack: options?.errorStack,
    offendingKeys: options?.offendingKeys,
    ts: Date.now(),
  };

  const events = [...diagnosticsState.lifecycleEvents, event];
  if (events.length > MAX_LIFECYCLE_EVENTS) {
    events.shift();
  }

  diagnosticsState = { ...diagnosticsState, lifecycleEvents: events };
  notifySubscribers();
}

// =============================================================================
// VALIDATION
// =============================================================================

/**
 * Validate a raw value against its enum (if defined in registry)
 */
export function validateRawValue(
  questionId: string,
  rawPath: string,
  value: unknown
): void {
  if (!diagnosticsState.enabled) return;

  const result = validateEnumValue(questionId, rawPath, value);
  if (!result.valid && result.message) {
    recordWarning({
      type: 'enum_mismatch',
      questionId,
      path: rawPath,
      message: result.message,
      details: { value, questionId, rawPath },
      ts: Date.now(),
    });
  }
}

/**
 * Check if a question is registered
 */
export function validateQuestionRegistered(questionId: string): void {
  if (!diagnosticsState.enabled) return;

  const mapping = getMappingByQuestionId(questionId);
  if (!mapping) {
    recordWarning({
      type: 'unmapped_question',
      questionId,
      message: `Question "${questionId}" is not registered in the mapping registry`,
      ts: Date.now(),
    });
  }
}

/**
 * Check for unexpected raw paths (paths written that aren't in registry)
 */
export function validateRawPath(path: string): void {
  if (!diagnosticsState.enabled) return;

  // Check if any mapping uses this path
  const usedByMapping = questionMappings.some(m => m.rawPaths.includes(path));

  if (!usedByMapping && !isAllowlisted(path)) {
    recordWarning({
      type: 'unexpected_raw_path',
      path,
      message: `Raw path "${path}" is not registered in any mapping and not allowlisted`,
      ts: Date.now(),
    });
  }
}

// =============================================================================
// INSTRUMENTED OPERATIONS
// =============================================================================

/**
 * Track a raw input change (CollectedData update)
 */
export function trackRawChange(
  questionId: string,
  path: string,
  prevValue: unknown,
  nextValue: unknown
): void {
  if (!diagnosticsState.enabled) return;

  // Validate the question is registered
  validateQuestionRegistered(questionId);

  // Validate the path is expected
  validateRawPath(path);

  // Validate enum value if applicable
  validateRawValue(questionId, path, nextValue);

  // Record the change
  recordChange({
    questionId,
    path,
    layer: 'raw',
    prev: prevValue,
    next: nextValue,
    ts: Date.now(),
  });
}

/**
 * Track a normalized input change (SystemRecInput update)
 */
export function trackNormalizedChange(
  questionId: string,
  path: string,
  prevValue: unknown,
  nextValue: unknown
): void {
  if (!diagnosticsState.enabled) return;

  recordChange({
    questionId,
    path,
    layer: 'normalized',
    prev: prevValue,
    next: nextValue,
    ts: Date.now(),
  });
}

/**
 * Update the raw input snapshot and trigger normalization
 * 
 * This is the centralized function that:
 * 1. Updates raw input snapshot
 * 2. Runs normalization (convertToSystemRecInput)
 * 3. Emits normalize_ok or normalize_failed events
 * 4. Updates normalized input snapshot
 * 
 * This ensures normalization happens reliably whenever raw data changes,
 * regardless of which route/context initiated the change.
 */
export function updateRawInputSnapshot(rawInput: Record<string, unknown>, options?: { docId?: string; contractVersion?: string | null; migratedFromLegacy?: boolean }): void {
  if (!diagnosticsState.enabled) return;

  // Update raw snapshot (and doc contract version / migration flag if provided)
  const contractVersionUpdate = options && 'contractVersion' in options
    ? { docContractVersion: options.contractVersion ?? null }
    : {};
  const migrationUpdate = options && 'migratedFromLegacy' in options
    ? { migratedFromLegacy: options.migratedFromLegacy ?? false }
    : {};
  diagnosticsState = { ...diagnosticsState, rawInput, ...contractVersionUpdate, ...migrationUpdate };

  // Run normalization
  try {
    // Convert to normalized input
    const normalizedInput = convertToSystemRecInput(rawInput);
    
    // Count keys
    const rawKeys = Object.keys(rawInput).length;
    const normalizedKeys = Object.keys(normalizedInput).length;
    
    // Emit success event
    trackLifecycleEvent('normalize_ok', 'mappingStore:normalize', {
      docId: options?.docId || '<local-draft>',
      keys: normalizedKeys,
      note: `Successfully normalized ${rawKeys} raw keys to ${normalizedKeys} normalized keys`,
    });
    
    // Update normalized snapshot
    // Note: Double cast is required because SystemRecInput is a specific interface
    // while diagnosticsState.normalizedInput expects Record<string, unknown>
    diagnosticsState = { ...diagnosticsState, normalizedInput: normalizedInput as unknown as Record<string, unknown> };
  } catch (error) {
    // Track normalization failure
    const errorMessage = error instanceof Error ? error.message : String(error);
    const errorStack = error instanceof Error ? error.stack : undefined;
    
    trackLifecycleEvent('normalize_failed', 'mappingStore:normalize', {
      docId: options?.docId || '<local-draft>',
      errorMessage,
      errorStack,
      offendingKeys: Object.keys(rawInput).slice(0, 10),
      note: `Normalization failed: ${errorMessage}`,
    });
    
    // Keep normalized as null on failure
    diagnosticsState = { ...diagnosticsState, normalizedInput: null };
  }
  
  notifySubscribers();
}

/**
 * Update the normalized input snapshot
 */
export function updateNormalizedInputSnapshot(normalizedInput: Record<string, unknown>): void {
  if (!diagnosticsState.enabled) return;

  diagnosticsState = { ...diagnosticsState, normalizedInput };
  notifySubscribers();
}

// =============================================================================
// DROPPED FIELD DETECTION
// =============================================================================

/**
 * Detect fields that changed in raw but not in normalized
 * This catches the "15mm → unknown" bug pattern
 */
export function detectDroppedFields(
  prevRaw: Record<string, unknown>,
  nextRaw: Record<string, unknown>,
  prevNormalized: Record<string, unknown>,
  nextNormalized: Record<string, unknown>
): void {
  if (!diagnosticsState.enabled) return;

  // Find all fields that changed in raw
  const changedRawPaths: string[] = [];
  findChangedPaths(prevRaw, nextRaw, '', changedRawPaths);

  // For each changed raw field, check if corresponding normalized field changed
  for (const rawPath of changedRawPaths) {
    // Find the mapping for this raw path
    const mapping = questionMappings.find(m => m.rawPaths.includes(rawPath));

    if (!mapping) {
      // Not in registry - handled by validateRawPath
      continue;
    }

    if (mapping.uiOnly) {
      // UI-only fields don't need to affect normalized
      continue;
    }

    if (mapping.normalizedPaths.length === 0) {
      // No normalized paths defined - check allowlist
      if (!isAllowlisted(rawPath)) {
        recordWarning({
          type: 'dropped_field',
          questionId: mapping.questionId,
          path: rawPath,
          message: `Raw field "${rawPath}" changed but has no normalized mapping (and is not UI-only or allowlisted)`,
          details: {
            prevValue: getNestedValue(prevRaw, rawPath),
            nextValue: getNestedValue(nextRaw, rawPath),
          },
          ts: Date.now(),
        });
      }
      continue;
    }

    // Check if any normalized path changed
    const normalizedChanged = mapping.normalizedPaths.some(normPath => {
      const prevNormValue = getNestedValue(prevNormalized, normPath);
      const nextNormValue = getNestedValue(nextNormalized, normPath);
      return !deepEqual(prevNormValue, nextNormValue);
    });

    if (!normalizedChanged) {
      recordWarning({
        type: 'dropped_field',
        questionId: mapping.questionId,
        path: rawPath,
        message: `Raw field "${rawPath}" changed but normalized field(s) [${mapping.normalizedPaths.join(', ')}] did not update`,
        details: {
          rawPrev: getNestedValue(prevRaw, rawPath),
          rawNext: getNestedValue(nextRaw, rawPath),
          normalizedPaths: mapping.normalizedPaths,
        },
        ts: Date.now(),
      });
    }
  }
}

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Find all paths that changed between two objects
 */
function findChangedPaths(
  prev: unknown,
  next: unknown,
  currentPath: string,
  results: string[]
): void {
  if (typeof prev !== 'object' || typeof next !== 'object' || prev === null || next === null) {
    if (!deepEqual(prev, next)) {
      results.push(currentPath);
    }
    return;
  }

  const allKeys = new Set([...Object.keys(prev as object), ...Object.keys(next as object)]);

  for (const key of allKeys) {
    const prevValue = (prev as Record<string, unknown>)[key];
    const nextValue = (next as Record<string, unknown>)[key];
    const newPath = currentPath ? `${currentPath}.${key}` : key;

    if (!deepEqual(prevValue, nextValue)) {
      if (typeof prevValue === 'object' && typeof nextValue === 'object' &&
          prevValue !== null && nextValue !== null && !Array.isArray(prevValue)) {
        findChangedPaths(prevValue, nextValue, newPath, results);
      } else {
        results.push(newPath);
      }
    }
  }
}

/**
 * Get a nested value from an object using dot notation
 */
function getNestedValue(obj: unknown, path: string): unknown {
  if (!obj || typeof obj !== 'object') return undefined;

  const parts = path.split('.');
  let current: unknown = obj;

  for (const part of parts) {
    if (current === null || current === undefined) return undefined;
    if (typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[part];
  }

  return current;
}

/**
 * Deep equality check
 */
function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === null || b === null) return false;
  if (typeof a !== typeof b) return false;

  if (typeof a === 'object') {
    if (Array.isArray(a) !== Array.isArray(b)) return false;

    if (Array.isArray(a)) {
      if (a.length !== (b as unknown[]).length) return false;
      return a.every((val, i) => deepEqual(val, (b as unknown[])[i]));
    }

    const keysA = Object.keys(a as object);
    const keysB = Object.keys(b as object);
    if (keysA.length !== keysB.length) return false;

    return keysA.every(key =>
      deepEqual(
        (a as Record<string, unknown>)[key],
        (b as Record<string, unknown>)[key]
      )
    );
  }

  return false;
}

// =============================================================================
// INITIALIZATION
// =============================================================================

/**
 * Initialize instrumentation based on environment
 * Call this at app startup
 * 
 * RUNTIME ONLY - no build-time gating
 */
export function initializeInstrumentation(): void {
  const hasDevFlag = typeof window !== 'undefined' &&
    (new URLSearchParams(window.location.search).get('dev') === '1' ||
     localStorage.getItem('devtools') === '1');

  if (hasDevFlag) {
    setInstrumentationEnabled(true);
    
    // Track devMode evaluation
    const urlDev = new URLSearchParams(window.location.search).get('dev');
    const localDev = localStorage.getItem('devtools');
    const reason = urlDev === '1' 
      ? 'URL param ?dev=1 found' 
      : localDev === '1'
        ? 'localStorage.devtools=1 persisted'
        : 'unknown';
    
    trackLifecycleEvent('devMode_evaluated', 'instrumentation.ts', {
      note: `Dev mode enabled: ${reason}`,
    });
    
    // Add visible indicator
    if (typeof document !== 'undefined') {
      document.body.dataset.devtools = 'enabled';
    }
    console.log('[Mapping] Instrumentation enabled');
  }
}

// =============================================================================
// EXPORTS FOR REGISTRY VALIDATION
// =============================================================================

/**
 * Get all registered question IDs (for audit)
 */
export function getRegisteredQuestionIds(): string[] {
  return getAllQuestionIds();
}
